<template>
  <ContentWrap>
    <h3 class="mb-18px">{{ contentTitle }}</h3>
    <el-form :model="query" inline>
      <el-form-item label="商圈名称"
        ><BusinessLookupSelect v-model="query.circleId" type="circles" placeholder="请选择商圈"
      /></el-form-item>
      <el-form-item label="状态">
        <el-select v-model="query.status" clearable class="!w-130px">
          <el-option
            v-for="(label, value) in currentStatusLabels"
            :key="value"
            :label="label"
            :value="Number(value)"
          />
        </el-select>
      </el-form-item>
      <el-button type="primary" @click="search">查询</el-button>
      <el-button v-hasPermi="[permission('create')]" @click="openForm()"
        >新增</el-button
      >
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list">
      <el-table-column prop="id" label="编号" width="90" />
      <el-table-column label="商圈名称" min-width="140"><template #default="scope"><BusinessLookupName type="circles" :id="scope.row.circleId" /></template></el-table-column>
      <el-table-column label="标题" min-width="180"
        ><template #default="scope">{{ titleOf(scope.row) }}</template></el-table-column
      >
      <el-table-column
        v-if="query.contentType === 'WHITEPAPER'"
        prop="versionNo"
        label="版本"
        width="80"
      />
      <el-table-column
        v-if="query.contentType === 'HISTORY'"
        prop="eventDate"
        label="历程日期"
        width="120"
      />
      <el-table-column label="状态" width="100"
        ><template #default="scope">{{
          currentStatusLabels[scope.row.status]
        }}</template></el-table-column
      >
      <el-table-column prop="updateTime" label="更新时间" width="180" />
      <el-table-column label="操作" width="210">
        <template #default="scope">
          <el-button
            v-hasPermi="[permission('update')]"
            link
            type="primary"
            @click="openForm(scope.row)"
            >修改</el-button
          >
          <template v-if="query.contentType !== 'HISTORY'">
            <el-button
              v-if="scope.row.status !== 1"
              v-hasPermi="[permission('publish')]"
              link
              type="success"
              @click="changeStatus(scope.row, 1)"
              >发布</el-button
            >
            <el-button
              v-if="scope.row.status === 1"
              v-hasPermi="[permission('publish')]"
              link
              type="warning"
              @click="changeStatus(scope.row, 2)"
              >下线</el-button
            >
          </template>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="query.pageNo"
      v-model:limit="query.pageSize"
      :total="total"
      @pagination="load"
    />
  </ContentWrap>

  <Dialog v-model="formVisible" :title="form.id ? '修改商圈内容' : '新增商圈内容'" width="680px">
    <el-form ref="formRef" :model="form" :rules="rules" label-width="90px">
      <el-form-item label="商圈名称" prop="circleId"
        ><BusinessLookupSelect v-model="form.circleId" type="circles" placeholder="请选择商圈"
      /></el-form-item>
      <el-form-item label="标题" prop="title"
        ><el-input v-model="form.title" maxlength="200"
      /></el-form-item>
      <el-form-item v-if="query.contentType === 'WHITEPAPER'" label="版本号" prop="versionNo"
        ><el-input-number v-model="form.versionNo" :min="1"
      /></el-form-item>
      <el-form-item v-if="query.contentType === 'ANNOUNCEMENT'" label="置顶"
        ><el-switch v-model="form.topFlag"
      /></el-form-item>
      <el-form-item v-if="query.contentType === 'HISTORY'" label="历程日期" prop="eventDate"
        ><el-date-picker v-model="form.eventDate" value-format="YYYY-MM-DD"
      /></el-form-item>
      <el-form-item v-if="query.contentType === 'HISTORY'" label="排序"
        ><el-input-number v-model="form.sortOrder" :min="0"
      /></el-form-item>
      <el-form-item v-if="query.contentType === 'HISTORY'" label="展示"
        ><el-switch v-model="historyVisible"
      /></el-form-item>
      <el-form-item label="内容" prop="content"
        ><el-input v-model="form.content" type="textarea" :rows="10"
      /></el-form-item>
    </el-form>
    <template #footer
      ><el-button @click="formVisible = false">取消</el-button
      ><el-button type="primary" @click="submit">保存</el-button></template
    >
  </Dialog>
</template>

<script setup lang="ts">
import * as ContentApi from '@/api/business/circleContent'

defineOptions({ name: 'BossCircleCircleContent' })
const route = useRoute()
const message = useMessage()
const loading = ref(false)
const list = ref<any[]>([])
const total = ref(0)
const formVisible = ref(false)
const formRef = ref()
const contentTypeOf = (path: string) =>
  path.endsWith('/circle-announcement')
    ? 'ANNOUNCEMENT'
    : path.endsWith('/circle-history')
      ? 'HISTORY'
      : 'WHITEPAPER'
const query = reactive({
  pageNo: 1,
  pageSize: 10,
  contentType: contentTypeOf(route.path),
  circleId: undefined,
  status: undefined
})
const form = reactive<any>({})
const historyVisible = computed({
  get: () => form.status === 1,
  set: (value) => (form.status = value ? 1 : 0)
})
const publishLabels: Record<number, string> = { 0: '草稿', 1: '已发布', 2: '已下线' }
const historyLabels: Record<number, string> = { 0: '隐藏', 1: '展示' }
const currentStatusLabels = computed(() =>
  query.contentType === 'HISTORY' ? historyLabels : publishLabels
)
const contentTitle = computed(() =>
  query.contentType === 'ANNOUNCEMENT'
    ? '公告管理'
    : query.contentType === 'HISTORY'
      ? '发展历程'
      : '白皮书管理'
)
const permission = (action: string) =>
  `bosscircle:${query.contentType.toLowerCase()}:${action}`
const rules = {
  circleId: [{ required: true, message: '请选择商圈' }],
  title: [{ required: true, message: '请输入标题' }],
  content: [{ required: true, message: '请输入内容' }]
}
const titleOf = (row: any) => row.title || row.eventTitle
const contentOf = (row: any) => row.content ?? row.eventContent
const load = async () => {
  loading.value = true
  try {
    const data = await ContentApi.getCircleContentPage(query)
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}
const search = () => {
  query.pageNo = 1
  load()
}
const openForm = (row?: any) => {
  Object.assign(
    form,
    row
      ? { ...row, title: titleOf(row), content: contentOf(row) }
      : {
          id: undefined,
          circleId: undefined,
          title: '',
          content: '',
          versionNo: 1,
          topFlag: false,
          eventDate: undefined,
          sortOrder: 0,
          status: query.contentType === 'HISTORY' ? 1 : 0,
          version: undefined
        }
  )
  form.contentType = query.contentType
  formVisible.value = true
}
const submit = async () => {
  await formRef.value.validate()
  if (form.id) await ContentApi.updateCircleContent(form)
  else await ContentApi.createCircleContent(form)
  message.success('保存成功')
  formVisible.value = false
  await load()
}
const changeStatus = async (row: any, status: number) => {
  await message.confirm(status === 1 ? '确认发布该内容吗？' : '确认下线该内容吗？')
  await ContentApi.publishCircleContent({
    contentType: query.contentType,
    id: row.id,
    version: row.version,
    status
  })
  message.success(status === 1 ? '发布成功' : '下线成功')
  await load()
}
onMounted(load)
watch(
  () => route.path,
  (path) => {
    query.contentType = contentTypeOf(path)
    query.pageNo = 1
    query.status = undefined
    load()
  }
)
</script>
